import {
  DEFAULT_GALLERY_COLUMN_COUNT,
  MAX_GALLERY_COLUMN_COUNT,
  normalizeGalleryConfig,
  stringifyGalleryConfig,
  type GalleryConfig,
  type GalleryEditRequest,
  type GalleryItem,
} from '#components/document-viewer/gallery/gallery-config';

export const NP_GALLERY_TAG = 'np-gallery';
export const NP_GALLERY_OPEN_TAG = `<${NP_GALLERY_TAG}>`;
export const NP_GALLERY_CLOSE_TAG = `</${NP_GALLERY_TAG}>`;

export type GalleryBlockData = GalleryConfig;
export type GalleryBlockItem = GalleryItem;

export {
  type GalleryEditRequest,
  DEFAULT_GALLERY_COLUMN_COUNT,
  MAX_GALLERY_COLUMN_COUNT,
};

export {
  normalizeGalleryConfig as normalizeGalleryBlockData,
  stringifyGalleryConfig as stringifyMarkdownGalleryConfig,
};

export interface ParsedMarkdownGalleryConfig {
  data: GalleryConfig;
  rawConfig: string;
  parseError: string | null;
}

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

export function parseMarkdownGalleryConfig(
  rawConfig: string,
): ParsedMarkdownGalleryConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeGalleryConfig({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeGalleryConfig(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeGalleryConfig({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse gallery JSON',
    };
  }
}

export function serializeMarkdownGalleryBlock(params: {
  data: GalleryConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_GALLERY_OPEN_TAG}${serializeMarkdownGalleryInnerConfig(params)}${NP_GALLERY_CLOSE_TAG}`;
}

export function serializeMarkdownGalleryInnerConfig(params: {
  data: GalleryConfig;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return params.parseError && params.rawConfig?.trim()
    ? normalizeRawConfig(params.rawConfig)
    : minifyJsonConfig(normalizeGalleryConfig(params.data));
}

export function createDefaultMarkdownGalleryValue(): string {
  return serializeMarkdownGalleryBlock({
    data: normalizeGalleryConfig({}),
  });
}
